import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import RouteReplayModal from "../components/RouteReplayModal";
import { Truck as TruckIcon, MapPin, GasPump, WarningOctagon, ArrowLeft, Path, Drop } from "@phosphor-icons/react";
import { formatDate, formatDateTime } from "../lib/format";
import { countryInfo } from "../context/CountryContext";

// Truck detail — one-stop view for a single truck: identity, live position, cumulative
// mileage, recent trips / dispenses / alerts. Route replay picker preserves the truck.
export default function TruckDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [replayOpen, setReplayOpen] = useState(false);
  const [replayInitial, setReplayInitial] = useState({ from: null, to: null });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/trucks/${id}/detail`)
      .then(({ data }) => { if (alive) { setData(data); setErr(""); } })
      .catch((e) => { if (alive) { setData(null); setErr(e?.response?.data?.detail || e.message); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const openReplay = (days) => {
    const to = new Date();
    const from = new Date(); from.setDate(from.getDate() - (days - 1));
    setReplayInitial({ from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) });
    setReplayOpen(true);
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (err || !data) return (
    <div className="p-8">
      <div className="bg-red-50 border border-red-200 text-red-700 rounded p-4">Failed to load truck: {err || "not found"}</div>
      <button onClick={() => navigate("/trucks")} className="mt-4 text-ps-blue hover:underline text-sm">← Back to fleet</button>
    </div>
  );

  const { truck, mileage, latest_position, recent_trips, recent_dispenses, recent_alerts } = data;
  const isStation = truck.asset_type === "station";

  return (
    <div>
      <PageHeader
        eyebrow="Truck"
        title={truck.vehicle_number}
        subtitle={truck.license_plate || truck.imei ? `${truck.license_plate || ""}${truck.license_plate && truck.imei ? " · " : ""}${truck.imei ? `GPS ${truck.imei}` : ""}` : `Registered ${formatDate(truck.created_at)}`}
        actions={
          <button onClick={() => navigate("/trucks")} data-testid="truck-back" className="text-slate-500 hover:text-ps-deep text-sm flex items-center gap-1"><ArrowLeft size={14} weight="bold" /> Back to fleet</button>
        }
      />

      <div className="p-6 space-y-6">

        {/* Top row: identity, live position, mileage */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <IdentityCard truck={truck} />
          {!isStation && <LivePositionCard latest={latest_position} vehicleNumber={truck.vehicle_number} />}
          <MileageCard
            mileage={mileage}
            onReplay={openReplay}
            isStation={isStation}
          />
        </div>

        {/* Trips */}
        <RecentList
          title="Recent trips"
          icon={<Path size={16} weight="duotone" />}
          testid="truck-recent-trips"
          rows={recent_trips}
          emptyLabel="No trips for this truck yet."
          render={(r) => (
            <Link to={`/activities?focus=${r.id}`} className="grid grid-cols-[100px_1fr_130px_100px_90px] gap-3 items-center px-4 py-2 hover:bg-slate-50 border-b border-slate-100" data-testid={`truck-trip-${r.id}`}>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-data">{formatDate(r.date)}</span>
              <span className="text-sm text-ps-deep truncate">{r.client || "—"} · <span className="text-slate-500">{r.activity_detail || "—"}</span></span>
              <span className="text-xs text-slate-500 truncate">{r.driver_name || "—"}</span>
              <span className="text-xs font-mono-data tabular text-right">{r.fuel_actual_l != null ? (
                <span className={r.fuel_actual_l > (r.fuel_approved_l || 0) ? "text-ps-red" : "text-lime-700"}>{r.fuel_actual_l} L</span>
              ) : r.fuel_approved_l ? `${r.fuel_approved_l} L` : "—"}</span>
              <span className={`text-[10px] uppercase tracking-[0.2em] text-right font-semibold ${statusColor(r.status)}`}>{r.status}</span>
            </Link>
          )}
        />

        {/* Dispenses */}
        <RecentList
          title="Recent dispenses"
          icon={<GasPump size={16} weight="duotone" />}
          testid="truck-recent-dispenses"
          rows={recent_dispenses}
          emptyLabel="No dispenses yet."
          render={(r) => (
            <div className="grid grid-cols-[100px_1fr_100px_100px] gap-3 items-center px-4 py-2 border-b border-slate-100" data-testid={`truck-dispense-${r.id}`}>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-data">{formatDate(r.date)}</span>
              <span className="text-sm text-ps-deep truncate">{r.job_description || "—"}</span>
              <span className="text-xs font-mono-data tabular text-right">{r.total_litres || r.quantity} L</span>
              <span className="text-xs font-mono-data tabular text-right text-ps-blue">GHS {(r.total_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          )}
        />

        {/* Alerts */}
        {!isStation && (
          <RecentList
            title="Recent GPS alerts"
            icon={<WarningOctagon size={16} weight="duotone" />}
            testid="truck-recent-alerts"
            rows={recent_alerts}
            emptyLabel="No alerts — this truck is behaving."
            render={(r) => (
              <div className="grid grid-cols-[130px_1fr_100px] gap-3 items-center px-4 py-2 border-b border-slate-100" data-testid={`truck-alert-${r.id}`}>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-data">{r.ts?.slice(0, 16).replace("T", " ")}</span>
                <span className="text-sm text-ps-deep truncate">{r.reason || r.rule || "—"}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-right text-ps-red font-semibold">{r.severity || "info"}</span>
              </div>
            )}
          />
        )}
      </div>

      {replayOpen && (
        <RouteReplayModal
          vehicleNumber={truck.vehicle_number}
          initialFrom={replayInitial.from}
          initialTo={replayInitial.to}
          onClose={() => setReplayOpen(false)}
        />
      )}
    </div>
  );
}

function IdentityCard({ truck }) {
  const isStation = truck.asset_type === "station";
  return (
    <div className={`rounded-xl border p-5 ${isStation ? "bg-lime-50 border-lime-200" : "bg-white border-slate-200"}`} data-testid="truck-identity-card">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 grid place-items-center rounded-md ${isStation ? "bg-lime-100 text-lime-700" : "bg-ps-blue text-white"}`}>
          {isStation ? <Drop size={20} weight="duotone" /> : <TruckIcon size={20} weight="duotone" />}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{isStation ? "Yard equipment" : "Vehicle"}</div>
          <div className="font-mono-data font-bold text-lg tracking-wide text-ps-deep">{truck.vehicle_number}</div>
        </div>
      </div>
      <div className="mt-4 space-y-2 text-xs">
        <Row label="Country" value={<><span className="mr-1">{countryInfo(truck.country).flag}</span>{countryInfo(truck.country).name}</>} />
        {truck.license_plate && <Row label="License plate" value={truck.license_plate} mono />}
        {truck.imei && <Row label="GPS IMEI" value={truck.imei} mono />}
        {truck.capacity_litres != null && <Row label="Tank" value={`${truck.capacity_litres} L`} />}
        {truck.unit_label && <Row label="Unit label" value={truck.unit_label} />}
        {truck.notes && <Row label="Notes" value={truck.notes} />}
        <Row label="Registered" value={formatDate(truck.created_at)} />
      </div>
    </div>
  );
}

function LivePositionCard({ latest, vehicleNumber }) {
  if (!latest) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="truck-live-position">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1.5"><MapPin size={12} /> Live position</div>
        <div className="mt-2 text-slate-400 text-sm">No GPS data yet for {vehicleNumber}.</div>
      </div>
    );
  }
  const age = latest.ts ? Math.round((Date.now() - new Date(latest.ts).getTime()) / 60000) : null;
  const stale = age != null && age > 60;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="truck-live-position">
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1.5"><MapPin size={12} /> Live position</div>
      <div className="mt-2">
        <div className="text-sm text-ps-deep font-medium truncate" title={latest.address}>{latest.address || `${latest.lat.toFixed(4)}, ${latest.lng.toFixed(4)}`}</div>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
          <span className="font-mono-data tabular"><span className="text-slate-500">speed</span> {Math.round(latest.speed_kmh || 0)} km/h</span>
          {latest.engine_on != null && <span><span className="text-slate-500">engine</span> {latest.engine_on ? "ON" : "OFF"}</span>}
          <span className={stale ? "text-ps-red" : "text-slate-500"}>{age == null ? "—" : age < 60 ? `${age} min ago` : `${Math.round(age / 60)} h ago`}</span>
        </div>
        <a href={`https://www.google.com/maps?q=${latest.lat},${latest.lng}`} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[11px] text-ps-blue hover:underline">Open in Google Maps →</a>
      </div>
    </div>
  );
}

function MileageCard({ mileage, onReplay, isStation }) {
  if (isStation) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 opacity-70" data-testid="truck-mileage-card">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Mileage</div>
        <div className="mt-2 text-slate-400 text-sm">Yard equipment doesn&apos;t travel.</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="truck-mileage-card">
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Mileage from GPS</div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <MileageStat label="Last 7 days" km={mileage.week_km} testid="mileage-week" />
        <MileageStat label="Last 30 days" km={mileage.month_km} testid="mileage-month" />
        <MileageStat label="All-time" km={mileage.total_km} testid="mileage-total" />
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <button onClick={() => onReplay(1)} data-testid="mileage-replay-1" className="text-[10px] font-mono-data font-bold uppercase tracking-wide border border-ps-blue text-ps-blue px-2.5 py-1 rounded hover:bg-sky-50">Replay 1d</button>
        <button onClick={() => onReplay(7)} data-testid="mileage-replay-7" className="text-[10px] font-mono-data font-bold uppercase tracking-wide border border-ps-blue text-ps-blue px-2.5 py-1 rounded hover:bg-sky-50">Replay 7d</button>
        <button onClick={() => onReplay(30)} data-testid="mileage-replay-30" className="text-[10px] font-mono-data font-bold uppercase tracking-wide bg-ps-blue text-white px-2.5 py-1 rounded hover:bg-ps-deep">Replay 30d</button>
      </div>
    </div>
  );
}

function MileageStat({ label, km, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-mono-data tabular text-2xl font-bold text-ps-deep">{Math.round(km || 0).toLocaleString()}<span className="text-xs text-slate-500 ml-1">km</span></div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function RecentList({ title, icon, testid, rows, emptyLabel, render }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <span className="text-ps-blue">{icon}</span>
        <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">{title}</div>
        <span className="text-xs text-slate-400 font-mono-data">· {rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-500">{emptyLabel}</div>
      ) : (
        <div>{rows.map(render)}</div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500 w-28 shrink-0">{label}</span>
      <span className={`text-slate-800 ${mono ? "font-mono-data tabular" : ""}`}>{value}</span>
    </div>
  );
}

function statusColor(s) {
  switch (s) {
    case "completed": return "text-lime-700";
    case "cancelled": return "text-slate-400";
    case "rejected": return "text-ps-red";
    case "issued": case "awaiting_waybill": return "text-ps-blue";
    default: return "text-slate-500";
  }
}
